import OrderDTO from "@/dto/order/OrderDTO";
import OrderSellerRemarksDTO from "@/dto/order/OrderSellerRemarksDTO";
import FulfillmentType from "@/enum/FulfillmentType";
import { computeSelectAllStatus, isManualLockOrder, isMergeOrder } from "@/utils/OrderUtils";
import { Modal } from "antd";
import { FormInstance } from "antd";
import { computed, observable, makeObservable, actionBound, observableDeep } from "mobx";
import OrderSort from "../enum/OrderSort";
import OrderTab from "../enum/OrderTab";
import * as localConfig from "../redux/sagas/localConfig";
import TableSearchState from "../redux/states/TableSearchState";
import { ListOptionsState, OrderType, TablePagination } from "../redux/states/TableState";
import { chunk, cloneDeep, groupBy } from "lodash";
import { matchDoubleAddresses } from "@/services/doubleAddressChecker";
import { selectOrder, unselectOrder, hasChildOrder, selectChildOrderItem } from "@/utils/OrderSelectUtil";
import getOrdersByLocalSort from "../utils/getOrdersByLocalSort";
import { VirtualizedTableStore } from "@/scenes/OrderManagementForAllShop/components/VirtualizedTable";
import ProductFilterStore from "../components/OrderTable/ListOptionsPane/ProductFilter/store/ProductFilterStore";
import { getManualMergeableOrderCount } from "@/services/api/order";
import OrderLockStore from "@/store/OrderLockStore";
import OrderStatus from "@/enum/OrderStatus";

export const ShowShiftTipCache = "__ShowShiftTipCache__";

/**
 * 订单列表的表格管理类
 */
export default class OrderManagementTableStore {
    constructor() {
        makeObservable(this, {
            loading: observable,
            onVirtualizedTableStoreChange: actionBound,
            orders: observable,
            orderMapping: computed,
            selectAllStatus: computed,
            isShowMoreOptions: observable,
            shippingDeadlineChecked: observable,
            shippingOverdueChecked: observable,
            showAddressReachableCheckPane: observable,
            loadManualMergeableOrderCount: actionBound,
            calcDoubleAddress: actionBound,
            switchShowAddressReachableCheckPane: actionBound,
            switchShippingDeadlineChecked: actionBound,
            switchShippingOverdueChecked: actionBound,
            setOrders: actionBound,
            updateServerOrderCache: actionBound,
            selectedOrders: computed,
            selectedOrderCount: computed,
            getSelectedOrders: actionBound,
            activeCriteriaId: observable,
            setActiveCriteriaId: actionBound,
            injectFormInstance: actionBound,
            listOptions: observableDeep,
            pagination: observable,
            getOrderTableConfig: actionBound,
            changeFulfillmentType: actionBound,
            changeOrderSort: actionBound,
            changeLocalOrderSort: actionBound,
            updateOrder: actionBound,
            updateOrders: actionBound,
            updateSellerRemarks: actionBound,
            updateSupplierOrderRemarks: actionBound,
            toggleMoreSearchOptions: actionBound,
            changePageSize: actionBound,
            changePageNo: actionBound,
            toggleOrderSelect: actionBound,
            selectOrder: actionBound,
            unselectOrder: actionBound,
            selectAllOrders: actionBound,
            unselectAllOrders: actionBound,
            selectOrderItem: actionBound,
            unselectOrderItem: actionBound,
            shiftPressed: observable,
            lastSelectedIndex: observable,
            lastUnselectedIndex: observable,
            updateShiftPressed: actionBound,
            updateLastSelectedIndex: actionBound,
            updateLastUnSelectedIndex: actionBound,
            hintShiftTipOrderId: observable,
            disabledShowShiftTip: observable,
            handleNeverShopShiftTip: actionBound,
            clickCount: actionBound,
            clearClickCountEffect: actionBound,
            changeOrderSelect: actionBound,
        });
    }

    loading?: boolean = false;

    virtualizedTableStore?: VirtualizedTableStore<OrderDTO>;

    productFilterStore: ProductFilterStore = new ProductFilterStore();

    onVirtualizedTableStoreChange(virtualizedTableStore: VirtualizedTableStore<OrderDTO>) {
        this.virtualizedTableStore = virtualizedTableStore;
    }

    orderLockStore = new OrderLockStore();

    orders?: OrderType[] = []; // 订单

    get orderMapping() {
        const mapping = new Map<number, OrderDTO>();

        this.orders?.forEach((order) => {
            mapping.set(order.id, order);
        });

        return mapping;
    }

    get selectAllStatus() {
        return computeSelectAllStatus(this.orders, this.orderLockStore.lockOrderIdMapping);
    }

    isShowMoreOptions = false;

    shippingDeadlineChecked: boolean;

    shippingOverdueChecked: boolean;

    /**
     * 显示快递范围检测栏
     */
    showAddressReachableCheckPane: boolean = false;

    async loadManualMergeableOrderCount(orders: OrderDTO[], tab: OrderTab) {
        if (!orders?.length) return;

        if (![OrderTab.PENDING_SHIPMENT, OrderTab.ON_HOLD, OrderTab.PENDING_SHIPMENT_REFUNDING].includes(tab)) {
            return;
        }

        const group = groupBy(orders, "shopId");

        for (const orders of Object.values(group)) {
            const orderMap = new Map<number, OrderDTO>();
            orders.forEach((order) => {
                orderMap.set(order.id, order);
            });

            const orderChunks = chunk(orders, 100);
            for (const orderChunk of orderChunks) {
                const shopId = orderChunk[0].shopId;
                const manualMergeableOrderCounts = await getManualMergeableOrderCount(
                    shopId,
                    orderChunk.map(({ id, buyerUsername, consignee }) => ({
                        id,
                        buyerUsername,
                        consigneeHash: consignee?.hash,
                    })),
                );

                manualMergeableOrderCounts.forEach((it) => {
                    const order = orderMap.get(it.orderId);

                    if (!order) return;

                    const {
                        sameBuyerPendingPaymentCount,
                        sameBuyerMergeablePendingShipmentCount,
                        sameConsigneePendingPaymentCount,
                        sameConsigneeMergeablePendingShipmentCount,
                    } = it;

                    order.sameBuyerPendingPaymentCount = sameBuyerPendingPaymentCount;
                    order.sameBuyerMergeablePendingShipmentCount = sameBuyerMergeablePendingShipmentCount;
                    order.sameConsigneePendingPaymentCount = sameConsigneePendingPaymentCount;
                    order.sameConsigneeMergeablePendingShipmentCount = sameConsigneeMergeablePendingShipmentCount;
                });
            }
        }
    }

    async calcDoubleAddress(orders: OrderDTO[]) {
        const result = await matchDoubleAddresses(orders);

        console.log(result);

        orders?.forEach((order) => {
            const key = `${order.shopId}_${order.id}`;

            const data = result[key];

            if (data?.isMatch) {
                order.consignee._streetDoubleAddressKeywords = data.streetRangeInfo?.map((it) => it.keyword);
                order.consignee._localStreetDoubleAddressKeywords = data.localStreetRangeInfo?.map((it) => it.keyword);
            }
        });
    }

    switchShowAddressReachableCheckPane(bol: boolean) {
        this.showAddressReachableCheckPane = bol;
    }

    switchShippingDeadlineChecked(checked: boolean) {
        this.shippingDeadlineChecked = checked;
    }

    switchShippingOverdueChecked(checked: boolean) {
        this.shippingOverdueChecked = checked;
    }

    /**
     * (商品筛选|本地排序)功能专用缓存
     *
     * 功能背景:
     * 1. 商品筛选过滤操作不会触发服务端请求，直接使用客户端现有数据进行过滤
     * 2. 当用户修改订单状态后又取消筛选条件时，需要保证状态回显一致性
     *
     * 数据逻辑:
     * - 初始值: 存储服务端原始响应数据快照
     * - 更新机制: 当执行搜索/刷新等服务端请求时更新
     * - 同步机制: 本地订单状态变更后需同步到本缓存（通过 @see{@link updateServerOrderCache}）
     *
     * @todo
     * - 在【Order】中添加【visible】字段来控制其在列表中的显隐状态，避免维护额外副本变量的开销。
     */
    serverOrdersCache: OrderDTO[];

    /**
     * 设置订单列表数据并管理缓存
     *
     * @param orders - 新的订单数据集
     * @param cache - 是否设置订单缓存（默认为true）
     *                true:  更新缓存（用于搜索/刷新等服务端请求场景）
     *                false: 保持缓存不变（用于商品筛选本地过滤/回显场景）
     */
    /**
     * 设置订单列表的方法
     * @param orders - 订单数据传输对象(OrderDTO)数组
     * @param cache - 是否缓存订单数据，默认为true
     */
    setOrders(orders: OrderDTO[], cache = true) {
        this.orders = orders;

        /** 搜索订单后需要缓存订单数据（商品筛选中使用） */
        if (cache) {
            this.serverOrdersCache = orders;

            this.productFilterStore.setProductFilterOrderSortTypes([]);
        }
    }

    /**
     * 订单状态回写
     *
     * 将【商品筛选】的订单状态同步原订单数据快照，
     * 但不包含临时交互状态（勾选状态）。
     *
     */
    updateServerOrderCache() {
        if (!this.serverOrdersCache) return;

        this.orders.forEach((order) => {
            const index = this.serverOrdersCache.findIndex((cacheOrder) => cacheOrder.id === order.id);

            if (index > -1) {
                const cloneOrder = cloneDeep(order);
                /** 不需要复制是否勾选状态 */
                unselectOrder(cloneOrder);

                this.serverOrdersCache[index] = cloneOrder;
            }
        });
    }

    get selectedOrders() {
        return this.orders?.filter((it) => it.isSelected);
    }

    get selectedOrderCount() {
        return this.selectedOrders?.length || 0;
    }

    /**
     * 兼容方法
     */
    getSelectedOrders() {
        if (!this.selectedOrders?.length) {
            Modal.error({
                title: "请至少勾选一个订单",
            });

            return [];
        }

        return this.selectedOrders;
    }

    /**
     * 以下场景需要清空【activeCriteriaId】,并且清空时，需要一并将@see{@link TableSearchState.anyMatch}清空
     * 1.修改表单
     * 2.重置表单
     * 3.删除该Id对应的常用搜索时
     */
    activeCriteriaId: number = null;

    setActiveCriteriaId(id: number) {
        this.activeCriteriaId = id;

        if (!id && this.form) {
            this.form.resetFields?.(["anyMatch", "templateId"]);
        }
    }

    form?: FormInstance<TableSearchState>;

    resetFormValues(omitKeys?: (keyof TableSearchState)[]) {
        if (!this.form) {
            return;
        }

        if (omitKeys?.length) {
            const currentValues = this.form.getFieldsValue();
            const needResetKeys = [];

            Object.keys(currentValues).forEach((key: keyof TableSearchState) => {
                if (omitKeys.includes(key)) {
                    // break;
                    return;
                } else {
                    needResetKeys.push(key);
                }
            });

            if (needResetKeys.length) {
                this.form.resetFields(needResetKeys);
            }
        } else {
            this.form.resetFields();
        }
    }

    injectFormInstance(form: FormInstance<TableSearchState>) {
        this.form = form;
    }

    /**
     * 上次缓存的请求订单列表的参数 由search转换而来
     */
    prevQueryParams?: string;

    listOptions?: ListOptionsState = {
        fulfillmentType: FulfillmentType.ALL,
    };

    pagination: TablePagination = {
        pageNo: 1,
        pageSize: 25,
        totalCount: 0,
    };

    async getOrderTableConfig(shopId: number | undefined, tab: OrderTab) {
        try {
            const { pageSize } = await localConfig.get(shopId, tab);

            this.pagination.pageSize = pageSize;
            if (!this.listOptions.orderSort) {
                this.listOptions.orderSort = await localConfig.getOrderSort(shopId, tab);
            }
        } catch (e) {
            console.error(e);
        }
    }

    changeFulfillmentType(fulfillmentType: FulfillmentType) {
        this.listOptions.fulfillmentType = fulfillmentType;
    }

    changeOrderSort(shopId: number | undefined, tab: OrderTab, value: OrderSort) {
        this.listOptions.orderSort = value;

        localConfig.setOrderSort(shopId, tab, value);

        this.listOptions.localOrderSort = null;
    }

    changeLocalOrderSort(value: OrderSort) {
        this.listOptions.localOrderSort = value;

        this.updateServerOrderCache();

        if (!this.serverOrdersCache?.length) {
            return;
        }

        if (value) {
            const newOrders = getOrdersByLocalSort(this.serverOrdersCache, value);
            this.setOrders(newOrders, false);
        } else {
            this.setOrders(this.serverOrdersCache, false);
        }
    }

    /**
     * 更新单个订单
     */
    async updateOrder(orderId: number, orderObj: Partial<OrderType> = {}) {
        const order = this.orderMapping.get(orderId);

        if (!order) {
            return;
        }

        Object.keys(orderObj).forEach((key) => {
            order[key] = orderObj[key];
        });
    }

    /**
     * 批量更新订单
     */
    async updateOrders(orderIds: number[], orderObj: Partial<OrderType> = {}) {
        const orders = orderIds
            .map((orderId) => {
                const order = this.orderMapping.get(orderId);
                return order;
            })
            .filter((it) => !!it);

        if (!orders.length) {
            return;
        }

        Object.keys(orderObj).forEach((key) => {
            orders.forEach((order) => {
                order[key] = orderObj[key];
            });
        });
    }

    /**
     * 更新卖家备注
     */
    updateSellerRemarks(orderId: number, sellerRemarks: OrderSellerRemarksDTO) {
        const order = this.orderMapping.get(orderId);

        if (!order) {
            return;
        }

        order.sellerRemarks = sellerRemarks;

        if (order.childOrders) {
            order.childOrders = order.childOrders.map((childOrder) => ({
                ...childOrder,
                sellerRemarks,
            }));
        }
    }

    updateSupplierOrderRemarks(supplierOrderId: number, supplierRemarks: string) {
        const order = this.orderMapping.get(supplierOrderId);

        if (!order) {
            return;
        }

        if (order.supplierDropShippingInfo) {
            order.supplierDropShippingInfo.supplierRemarks = supplierRemarks;
        }
    }

    toggleMoreSearchOptions() {
        this.isShowMoreOptions = !this.isShowMoreOptions;
    }

    changePageSize(shopId: number, pageSize: number) {
        this.pagination.pageSize = pageSize;

        localConfig.setPageSize(shopId, pageSize);
    }

    changePageNo(pageNo: number) {
        this.pagination.pageNo = pageNo;
    }

    toggleOrderSelect(orderId: number, index: number) {
        const order = this.orderMapping.get(orderId);

        if (!order) {
            return;
        }

        const lock = this.orderLockStore.lockOrderIdMapping.get(order.id);

        // 锁单则跳过选择
        if (lock) {
            return;
        }

        if (order.isSelected) {
            this.changeOrderSelect(false, orderId, index);
        } else {
            this.changeOrderSelect(true, orderId, index);
        }
    }

    selectOrder(orderId: number) {
        const order = this.orderMapping.get(orderId);

        if (!order) {
            return;
        }

        const lock = this.orderLockStore.lockOrderIdMapping.get(order.id);

        // 锁单则跳过选择
        if (lock) {
            return;
        }

        selectOrder(order);
    }

    unselectOrder(orderId: number) {
        const order = this.orderMapping.get(orderId);

        if (!order) {
            return;
        }

        unselectOrder(order);
    }

    async selectAllOrders() {
        for (let j = 0; j < this.orders.length; j++) {
            const order = this.orders[j];

            const lock = this.orderLockStore.lockOrderIdMapping.get(order.id) || isManualLockOrder(order);

            // 锁单则跳过选择
            if (lock) {
                continue;
            }

            selectOrder(order);
        }
    }

    unselectAllOrders() {
        this.orders.forEach((order) => unselectOrder(order));
    }

    selectOrderItem(orderId: number, orderItemId: number) {
        this.orders.forEach((order) => {
            const lock = this.orderLockStore.lockOrderIdMapping.get(order.id);

            // 锁单则跳过选择
            if (lock) {
                return;
            }

            if (order.id === orderId || hasChildOrder(order, orderId)) {
                order.isSelected = true;

                order.orderItems?.forEach((orderItem) => {
                    if (orderItem.id === orderItemId) {
                        // @ts-ignore
                        orderItem.isSelected = true;
                    }
                });

                // 1.锁单不支持勾选
                // 2.只要不在已取消Tab下，那么已被取消的 OrderItem 就不能被勾选
                const allSelectableOrderItems = order.orderItems?.filter((orderItem) => {
                    let selectable = true;

                    if (order.status !== OrderStatus.CANCELED) {
                        selectable = orderItem.status !== OrderStatus.CANCELED;
                    }
                    return selectable;
                });

                order._isPartSelected = !(allSelectableOrderItems || []).every((orderItem) => orderItem.isSelected);

                selectChildOrderItem(order.childOrders, orderItemId, true);
            }
        });
    }

    unselectOrderItem(orderId: number, orderItemId: number) {
        this.orders.forEach((order) => {
            if (order.id === orderId || hasChildOrder(order, orderId)) {
                let isAnyOrderItemSelected = false;
                order.orderItems?.forEach((orderItem) => {
                    if (orderItem.id === orderItemId) {
                        // @ts-ignore
                        orderItem.isSelected = false;
                    } else {
                        // @ts-ignore
                        if (orderItem.isSelected) {
                            isAnyOrderItemSelected = true;
                        }
                    }
                });

                order.isSelected = isAnyOrderItemSelected;
                order._isPartSelected = isAnyOrderItemSelected;

                selectChildOrderItem(order.childOrders, orderItemId, false);
            }
        });
    }

    /** Shift操作订单相关 */
    shiftPressed = false;

    lastSelectedIndex = null;

    lastUnselectedIndex = null;

    updateShiftPressed(shiftPressed: boolean) {
        this.shiftPressed = shiftPressed;
    }

    updateLastSelectedIndex(lastSelectedIndex: number | null) {
        this.lastSelectedIndex = lastSelectedIndex;
    }

    updateLastUnSelectedIndex(lastUnselectedIndex: number | null) {
        this.lastUnselectedIndex = lastUnselectedIndex;
    }

    //

    clickOrderIdSet = new Set<number>();

    hintShiftTipOrderId: number;

    dismissShiftTipTimer: NodeJS.Timeout;

    clearOrderIdSetTimer: NodeJS.Timeout;

    disabledShowShiftTip = localStorage.getItem(ShowShiftTipCache);

    handleNeverShopShiftTip() {
        localStorage.setItem(ShowShiftTipCache, "1");

        this.clearClickCountEffect();

        this.disabledShowShiftTip = "1";
    }

    clickCount(orderId: number) {
        this.clickOrderIdSet.add(orderId);

        if (this.clearOrderIdSetTimer) {
            clearTimeout(this.clearOrderIdSetTimer);
            this.clearOrderIdSetTimer = null;
        }

        this.clearOrderIdSetTimer = setTimeout(() => {
            this.clickOrderIdSet.clear();
        }, 3000);

        if (this.clickOrderIdSet.size >= 3) {
            const clickOrderIdList = [...this.clickOrderIdSet];

            const sameContext = clickOrderIdList.every((orderId) => this.orders?.some((it) => it.id === orderId));

            if (sameContext) {
                if (this.dismissShiftTipTimer) {
                    clearTimeout(this.dismissShiftTipTimer);

                    this.dismissShiftTipTimer = null;
                }

                this.hintShiftTipOrderId = clickOrderIdList[clickOrderIdList.length - 1];

                this.dismissShiftTipTimer = setTimeout(() => {
                    this.hintShiftTipOrderId = null;
                }, 3000);
            }
        }
    }

    clearClickCountEffect() {
        this.clickOrderIdSet.clear();
        this.hintShiftTipOrderId = null;

        if (this.dismissShiftTipTimer) {
            clearTimeout(this.dismissShiftTipTimer);
            this.dismissShiftTipTimer = null;
        }

        if (this.clearOrderIdSetTimer) {
            clearTimeout(this.clearOrderIdSetTimer);
            this.clearOrderIdSetTimer = null;
        }
    }

    changeOrderSelect(checked: boolean, clickOrderId: number, index: number) {
        if (checked) {
            if (this.shiftPressed && this.lastSelectedIndex !== null) {
                const start = Math.min(index, this.lastSelectedIndex);
                const end = Math.max(index, this.lastSelectedIndex);

                for (let i = start; i <= end; i++) {
                    const orderId = this.orders[i]?.id;

                    if (orderId) {
                        this.selectOrder(orderId);
                    }
                }
            } else {
                this.selectOrder(clickOrderId);

                this.updateLastSelectedIndex(index);
            }

            this.clickCount(clickOrderId);

            this.updateLastUnSelectedIndex(null);
        } else {
            if (this.shiftPressed && this.lastUnselectedIndex !== null) {
                const start = Math.min(index, this.lastUnselectedIndex);
                const end = Math.max(index, this.lastUnselectedIndex);

                for (let i = start; i <= end; i++) {
                    const orderId = this.orders[i]?.id;

                    if (orderId) {
                        this.unselectOrder(orderId);
                    }
                }
            } else {
                this.unselectOrder(clickOrderId);

                this.updateLastUnSelectedIndex(index);
            }

            this.clearClickCountEffect();

            this.updateLastSelectedIndex(null);
        }
    }

    /** ----------------- */
}
